import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from './api';
import { useAuth } from './AuthContext';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';
import { getStoredRef, clearStoredRef } from '../lib/growth';
import { logger } from '../lib/logger';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG';

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  const label = ['Too short', 'Weak', 'Okay', 'Good', 'Strong', 'Excellent'][score] || '';
  return { score, label };
}

function strengthBarColor(score) {
  if (score <= 1) return 'bg-red-400';
  if (score <= 2) return 'bg-yellow-400';
  return 'bg-[#4FBCC7]';
}

export default function Signup() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate('/app/dashboard', { replace: true });
  }, [user, loading, navigate]);

  const strength = passwordStrength(password);

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/app/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Please tell us your name.'); return; }
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email.'); return;
    }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords don\'t match.'); return; }

    setSubmitting(true);
    try {
      const ref = getStoredRef();
      const { user: u } = await authApi.signup({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        ref: ref || undefined,
      });
      if (ref) clearStoredRef();
      setUser(u);
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      logger.error('signup failed', err);
      setError(err.message || 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white flex items-center justify-center px-5 py-10" data-testid="signup-page">
      <div className="absolute inset-0 bg-[radial-gradient(800px_circle_at_30%_40%,rgba(0,76,84,0.18),transparent_60%)] pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-7">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white shadow-2xl mb-4">
            <img src={LOGO_URL} alt="Apex Athletic Mind" className="w-full h-full object-contain" />
          </div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#A5ACAF] font-semibold mb-2">
            Mental Performance App
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            Create your account
          </h1>
          <p className="text-white/55 text-[14px] leading-relaxed max-w-[34ch]">
            Free to start. Your AI coach, pre-game prep, and journal in one place.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-7">
          <Button
            onClick={handleGoogleSignIn}
            className="w-full h-11 bg-white hover:bg-white/95 text-[#0a0d0e] font-semibold rounded-lg shadow-md"
            data-testid="signup-google-button"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Sign up with Google
          </Button>

          <div className="flex items-center gap-3 my-5" aria-hidden="true">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-white/40 font-mono">or with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
            <div>
              <Label htmlFor="signup-name" className="text-[12px] text-white/60 mb-1.5 block">Name</Label>
              <Input
                id="signup-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                disabled={submitting}
                data-testid="signup-name"
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-email" className="text-[12px] text-white/60 mb-1.5 block">Email</Label>
              <Input
                id="signup-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                disabled={submitting}
                data-testid="signup-email"
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-password" className="text-[12px] text-white/60 mb-1.5 block">Password</Label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="h-11 pr-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                  disabled={submitting}
                  data-testid="signup-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  data-testid="signup-toggle-pw"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${strengthBarColor(strength.score)}`}
                      style={{ width: `${(strength.score / 5) * 100}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-white/45 mt-1" data-testid="signup-strength">{strength.label}</div>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="signup-confirm" className="text-[12px] text-white/60 mb-1.5 block">Confirm password</Label>
              <div className="relative">
                <Input
                  id="signup-confirm"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  className="h-11 pr-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                  disabled={submitting}
                  data-testid="signup-confirm"
                  required
                />
                {confirm && password === confirm && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4FBCC7]" aria-hidden="true" />
                )}
              </div>
            </div>

            {error && (
              <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="signup-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
              data-testid="signup-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</> : 'Create account'}
            </Button>
            <p className="text-center text-[11px] text-white/40">
              By creating an account, you agree to our Terms and Privacy Policy.
            </p>
          </form>

          <p className="text-center text-[12px] text-white/45 mt-5">
            Already have an account?{' '}
            <Link to="/app/login" className="text-[#4FBCC7] hover:text-white font-semibold" data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </div>

        <div className="text-center mt-5">
          <a href="/" className="text-sm text-white/50 hover:text-white transition-colors" data-testid="signup-back-to-site">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
